import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { jsonOrThrow } from '../apiJson';
import { formatCount } from '../categoryLabels';
import { trackSeeAllClick, trackSelectCategory } from '../ga';
import {
  premiumShopSearchPath,
  premiumShopWebsiteHost,
} from '../premiumShops';
import { ProductCard } from './ProductCard';
import { CategoryProductGridSkeleton } from './ProductCardSkeleton';

const LAZY_LOAD_ROOT_MARGIN = '400px 0px';
export const FEATURED_PREMIUM_MOBILE_PREVIEW = 4;
export const FEATURED_PREMIUM_DESKTOP_PREVIEW = 9;
/** Insert after this many subcategories on category hubs (1-based). */
export const FEATURED_PREMIUM_CATEGORY_INSERT_AFTER = 4;

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function featuredProductKey(product) {
  const id = product?.id;
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }
  const url = typeof product?.url === 'string' ? product.url.trim() : '';
  return url || product?.title || 'product';
}

/**
 * Featured premium shop rail: 4+View all (mobile) / 9+View all scroll (desktop ≥50rem).
 *
 * @param {{
 *   shop: {
 *     rootDomain: string,
 *     name: string,
 *     faviconUrl?: string,
 *     tagline?: string,
 *   },
 *   products?: object[]|null,
 *   total?: number|null,
 *   layoutVariant?: 'classic' | 'engaging',
 *   wideLayout?: boolean,
 *   outboundCampaign?: string,
 *   analyticsSource?: string,
 * }} props
 */
export function FeaturedPremiumShopSection({
  shop,
  products: productsProp = null,
  total: totalProp = null,
  layoutVariant = 'classic',
  wideLayout = false,
  outboundCampaign = 'homepage_featured_premium',
  analyticsSource = 'home',
}) {
  const sectionRef = useRef(null);
  const fetchStartedRef = useRef(false);
  const staticProducts = Array.isArray(productsProp) ? productsProp : null;
  const [preview, setPreview] = useState(() => {
    if (!staticProducts) {
      return null;
    }
    return {
      products: staticProducts,
      total:
        typeof totalProp === 'number' && Number.isFinite(totalProp)
          ? totalProp
          : staticProducts.length,
    };
  });
  const engaging = layoutVariant === 'engaging';
  const previewLimit = wideLayout
    ? FEATURED_PREMIUM_DESKTOP_PREVIEW
    : FEATURED_PREMIUM_MOBILE_PREVIEW;
  const shopName = typeof shop?.name === 'string' ? shop.name.trim() : 'Shop';
  const websiteHost = premiumShopWebsiteHost(shop);
  const viewAllPath = premiumShopSearchPath(shop);
  const headingId = `featured-premium-${websiteHost || 'shop'}-heading`;
  const titleText = `Featured · ${shopName}`;
  const tagline =
    typeof shop?.tagline === 'string' && shop.tagline.trim() !== ''
      ? shop.tagline.trim()
      : '';
  const analyticsKey = `featured-premium:${websiteHost}`;

  useEffect(() => {
    if (staticProducts) {
      setPreview({
        products: staticProducts,
        total:
          typeof totalProp === 'number' && Number.isFinite(totalProp)
            ? totalProp
            : staticProducts.length,
      });
      return undefined;
    }

    const el = sectionRef.current;
    if (!el || websiteHost === '') {
      return undefined;
    }

    fetchStartedRef.current = false;
    setPreview(null);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || fetchStartedRef.current) {
          return;
        }
        fetchStartedRef.current = true;

        const url = new URL('/api/products.php', window.location.origin);
        url.searchParams.set('website', websiteHost);
        url.searchParams.set('page', '1');
        url.searchParams.set('per_page', String(FEATURED_PREMIUM_DESKTOP_PREVIEW));
        url.searchParams.set('sort', 'featured');

        fetch(url.toString(), { cache: 'no-store' })
          .then((res) => jsonOrThrow(res))
          .then((data) => {
            const products = Array.isArray(data?.products) ? data.products : [];
            const total =
              typeof data?.total === 'number' && Number.isFinite(data.total)
                ? data.total
                : products.length;
            setPreview({ products, total });
          })
          .catch(() => {
            setPreview({ products: [], total: 0 });
          });
      },
      { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [websiteHost, staticProducts, totalProp]);

  const loading = preview === null;
  const allProducts = preview?.products ?? [];
  const products = allProducts.slice(0, previewLimit);
  const total = preview?.total ?? 0;
  const moreCount = Math.max(1, total - products.length);

  if (!loading && allProducts.length === 0) {
    return null;
  }

  const impressionSource =
    analyticsSource === 'home' || analyticsSource === 'homepage'
      ? 'homepage'
      : analyticsSource === 'category'
        ? 'category'
        : 'browse';

  const productCells = products.map((product, index) => (
    <li
      key={featuredProductKey(product) || `featured-premium-${index}`}
      className="landing-categories__cell"
    >
      <ProductCard
        product={product}
        layout="grid"
        outboundCampaign={outboundCampaign}
        premiumShop={shop}
        analyticsContext={{
          source: impressionSource,
        }}
      />
    </li>
  ));

  const viewAllCell = (
    <li className="landing-categories__cell landing-categories__cell--cta">
      <Link
        to={viewAllPath}
        className="landing-categories__more-tile"
        aria-label={`View all ${formatCount(total)} products from ${shopName}`}
        onClick={() => trackSeeAllClick(analyticsKey, analyticsSource)}
      >
        <span className="landing-categories__more-label">View all</span>
        <span className="landing-categories__more-plus" aria-hidden="true">
          +{formatCount(moreCount)}
        </span>
      </Link>
    </li>
  );

  return (
    <section
      ref={sectionRef}
      className={cx(
        'landing-categories__section',
        'landing-categories__section--deals-home',
        'landing-categories__section--featured-premium',
        wideLayout && 'landing-categories__section--deals-home-wide',
      )}
      aria-labelledby={headingId}
    >
      <header className="landing-categories__section-header">
        <div className="landing-categories__heading-block">
          <h2 id={headingId} className="landing-categories__heading">
            <Link
              className="landing-categories__heading-link"
              to={viewAllPath}
              aria-label={
                total > 0
                  ? `${titleText}, ${formatCount(total)} products`
                  : titleText
              }
              onClick={() => trackSelectCategory(analyticsKey, `${analyticsSource}_grid`)}
            >
              {typeof shop?.faviconUrl === 'string' && shop.faviconUrl.trim() !== '' && (
                <img
                  className="landing-categories__featured-premium-favicon"
                  src={shop.faviconUrl.trim()}
                  alt=""
                  width={18}
                  height={18}
                  loading="lazy"
                  decoding="async"
                />
              )}
              {titleText}
            </Link>
          </h2>
          {tagline !== '' && (
            <p className="landing-categories__featured-premium-tagline">{tagline}</p>
          )}
        </div>
        {engaging && formatCount(total) && (
          <span className="landing-categories__heading-count">
            {formatCount(total)}
          </span>
        )}
      </header>
      {loading ? (
        <CategoryProductGridSkeleton count={previewLimit} />
      ) : wideLayout ? (
        <div
          className="landing-categories__product-scroll"
          aria-label={`${shopName} featured products`}
        >
          <ul className="landing-categories__product-scroll-track landing-categories__mini-cards">
            {productCells}
            {viewAllCell}
          </ul>
        </div>
      ) : (
        <ul className="landing-categories__grid landing-categories__mini-cards">
          {productCells}
          {viewAllCell}
        </ul>
      )}
    </section>
  );
}
